import React, { useState, useRef } from "react";
import { Button, Menu, Select, Textarea, Tooltip } from "@opendate-app/setlist";

import NodeErrorContainer from "../CustomCampaigns/NodeErrorContainer";
import { flattenLiquidData } from "../../../shared/liquidUtils";
import SmsOptOutToggle from "../../../shared/SmsOptOutToggle";

const containerStyle = {
  background: "var(--sl-border-secondary)",
  gap: 1,
  boxShadow: "var(--sl-shadow-sm)",
  color: "var(--sl-text-primary)",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  minHeight: "600px",
};

const attributesColumnStyle = {
  background: "var(--sl-bg-primary)",
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 16,
};

const columnStyle = {
  background: "var(--sl-bg-primary)",
  display: "flex",
  flexDirection: "column",
  fontSize: "14px",
  minWidth: 0,
};

const detailRowStyle = {
  alignItems: "center",
  borderBottom: "1px solid var(--sl-border-secondary)",
  display: "flex",
  gap: 8,
  padding: "8px 12px",
  minHeight: 49,
};

const messageEditorStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: 12,
};

const previewContainerStyle = {
  display: "flex",
  flexDirection: "column",
  padding: "12px",
  gap: "4px",
};

const characterCountStyle = {
  alignSelf: "flex-end",
  color: "var(--sl-text-tertiary)",
  fontSize: "12px",
  marginBottom: "8px",
};

const messageStyle = {
  background: "var(--sl-bg-tertiary)",
  borderRadius: "24px 24px 24px 0px",
  color: "var(--sl-text-primary)",
  lineHeight: "16px",
  width: "85%",
  padding: "12px 20px",
  wordWrap: "break-word",
  whiteSpace: "pre-wrap",
  marginBottom: "8px",
};

const headerStyle = {
  borderBottom: "1px solid var(--sl-border-secondary)",
  color: "var(--sl-text-tertiary)",
  fontSize: 10,
  paddingBottom: 4,
  width: "100%",
};

const rowStyle = {
  fontSize: 12,
  overflow: "hidden",
  textOverflow: "ellipsis",
  textWrap: "nowrap",
  width: "100%",
};

const chunkMessage = (str, chunkSize = 160) => {
  const chunks = [];
  for (let i = 0; i < str.length; i += chunkSize) {
    chunks.push(str.slice(i, i + chunkSize));
  }
  return chunks;
};

const TemplateEditor = ({ mergeTags = [], templatableContext }) => {
  const {
    template,
    setTemplate,
    processedTemplate,
    sampleData,
    twilioMessagingServiceId,
    twilioMessagingServices,
    updateTwilioTemplate,
    to,
    processedTo,
  } = templatableContext();

  const [cursorPosition, setCursorPosition] = useState(null);
  const editorRef = useRef(null);

  const flattenedSampleData = flattenLiquidData(sampleData);

  const handleCursor = (event) => {
    setCursorPosition(event.target.selectionStart);
  };

  const insertTag = (tag) => {
    const position = cursorPosition ?? template.length;
    const nextPosition = position + tag.length;

    setTemplate(template.substring(0, position) + tag + template.substring(position));
    setCursorPosition(nextPosition);
    setTimeout(() => {
      const textarea = editorRef.current?.querySelector("textarea");
      textarea?.focus();
      textarea?.setSelectionRange(nextPosition, nextPosition);
    }, 0);
  };

  const selectedMessagingService = twilioMessagingServices.find(service => service.id === twilioMessagingServiceId);

  const messageChunks = chunkMessage(processedTemplate);

  return (
    <div style={containerStyle}>
      <div style={attributesColumnStyle}>
        <div style={headerStyle}>
          ATTRIBUTES
        </div>
        {flattenedSampleData.map(({ path, value }) => (
          !!value ? (
            <div key={path} style={rowStyle}>
              <div style={{ color: "var(--sl-text-quaternary)" }}>{path}</div>
              <div style={{ color: "var(--sl-text-brand)" }}>{`"${value}"`}</div>
            </div>
          ) : null
        ))}
      </div>
      <div style={columnStyle}>
        <div style={detailRowStyle}>
          {selectedMessagingService ? null : (
            <NodeErrorContainer errors={{ from: "No messaging service selected" }} />
          )}
          <b>From: </b>
          {twilioMessagingServices.length === 1 ? (
            <span>{twilioMessagingServices[0].name}</span>
          ) : (
            <div style={{ flex: 1, minWidth: 0 }}>
              <Select
                size="sm"
                aria-label="Messaging service"
                placeholder="Select a messaging service"
                options={twilioMessagingServices.map((messagingService) => ({ value: messagingService.id, label: messagingService.name }))}
                value={twilioMessagingServiceId || null}
                onChange={(id) => {
                  if (id !== twilioMessagingServiceId) { updateTwilioTemplate({ twilio_messaging_service_id: id }); }
                }}
              />
            </div>
          )}
        </div>
        <div style={detailRowStyle}><b>To: </b>{to}</div>
        <div style={messageEditorStyle} ref={editorRef}>
          <Textarea
            rows={10}
            resize="none"
            aria-label="SMS message"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            placeholder="Enter your text here..."
            onClick={handleCursor}
            onSelect={handleCursor}
          />
          {mergeTags.length > 0 && (
            <div style={{ alignSelf: "flex-end" }}>
              <Menu
                placement="bottom-end"
                items={mergeTags.map((tag, index) => ({ key: `${tag.value}-${index}`, label: tag.name, onClick: () => insertTag(tag.value) }))}
                trigger={<Button variant="link-color" size="sm">Merge Tags</Button>}
              />
            </div>
          )}
        </div>
        <SmsOptOutToggle text={template} onChange={setTemplate} divided />
      </div>
      <div style={columnStyle}>
        <div style={detailRowStyle}>
          <b>From: </b>
          {selectedMessagingService?.twilio_phone_numbers?.map(phoneNumber => phoneNumber.phone_number).join(", ")}
        </div>
        <div style={detailRowStyle}><b>To: </b>{processedTo}</div>
        <div style={previewContainerStyle}>
          {processedTemplate.length > 160 ? (
            <div style={{ alignSelf: "flex-end" }}>
              <Tooltip title="Your message is over 160 characters, it will be sent as multiple texts.">
                <span style={{ ...characterCountStyle, color: "var(--sl-text-error)" }} tabIndex={0}>
                  {processedTemplate.length} characters
                </span>
              </Tooltip>
            </div>
          ) : (
            <div style={characterCountStyle}>
              {processedTemplate.length} characters
            </div>
          )}
          {messageChunks.map((chunk, index) => (
            <div key={index} style={messageStyle}>
              {chunk}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
